import QRCode from 'qrcode';
import QRCodeStyling from 'qr-code-styling';

// Map our friendly style names to qr-code-styling dot/corner types.
const DOT_TYPES = {
  square: 'square',
  dots: 'dots',
  rounded: 'rounded',
  classy: 'classy',
  'classy-rounded': 'classy-rounded',
  'extra-rounded': 'extra-rounded',
};
const CORNER_TYPES = {
  square: 'square',
  dot: 'dot',
  'extra-rounded': 'extra-rounded',
};

export const QR_DOT_STYLES = [
  ['square', 'Classic squares'],
  ['dots', 'Dots'],
  ['rounded', 'Rounded'],
  ['classy', 'Classy'],
  ['classy-rounded', 'Classy rounded'],
  ['extra-rounded', 'Extra rounded'],
];
export const QR_EYE_STYLES = [
  ['square', 'Square eyes'],
  ['extra-rounded', 'Rounded eyes'],
  ['dot', 'Dot eyes'],
];

const blobToDataUrl = (blob) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });

/**
 * Generate a branded QR code as a PNG data URL (safe for html-to-image export).
 * Accepts either a legacy color string (backwards compatible) or an options object:
 *   { color, bgColor, dotStyle, eyeStyle, logo }
 */
export async function makeQrDataUrl(url, opts) {
  if (!url) return '';
  const o = typeof opts === 'string' ? { color: opts } : (opts || {});
  const dark = o.color || '#0A0F1C';
  const light = o.bgColor || '#FFFFFF';
  const dotStyle = DOT_TYPES[o.dotStyle] || 'square';
  const cornerStyle = CORNER_TYPES[o.eyeStyle] || 'square';
  const transparent = light === 'transparent';

  // Classic squares with no logo & no transparency -> use the lightweight `qrcode`
  // library (fast, dependency-light). Otherwise render with qr-code-styling.
  if (dotStyle === 'square' && cornerStyle === 'square' && !o.logo && !transparent) {
    try {
      return await QRCode.toDataURL(url, {
        width: 640, margin: 1, errorCorrectionLevel: 'H',
        color: { dark, light },
      });
    } catch (e) {
      return '';
    }
  }

  try {
    const styling = new QRCodeStyling({
      width: 640,
      height: 640,
      type: 'canvas',
      data: url,
      margin: 8,
      qrOptions: { errorCorrectionLevel: 'H' },
      image: o.logo || undefined,
      imageOptions: { crossOrigin: 'anonymous', margin: 6, imageSize: 0.32, hideBackgroundDots: true },
      dotsOptions: { color: dark, type: dotStyle },
      backgroundOptions: { color: transparent ? 'rgba(0,0,0,0)' : light },
      cornersSquareOptions: { color: dark, type: cornerStyle === 'dot' ? 'dot' : cornerStyle },
      cornersDotOptions: { color: dark, type: cornerStyle === 'square' ? 'square' : 'dot' },
    });
    const blob = await styling.getRawData('png');
    if (!blob) return '';
    return await blobToDataUrl(blob);
  } catch (e) {
    // Fallback to the simple generator so a QR always renders.
    try {
      return await QRCode.toDataURL(url, {
        width: 640, margin: 1, errorCorrectionLevel: 'H',
        color: { dark, light: transparent ? '#FFFFFF' : light },
      });
    } catch (err) {
      return '';
    }
  }
}
